import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { EventDescription } from '~/composables/types/event'
import type { TypesenseClientHandle } from '~/composables/search/useSearchClient'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useTypesenseHandle } from '~/composables/search/useTypesenseHandle'
import { fetchTypesenseSourceEvent } from '~/composables/search/useTypesenseSourceEvent'
import { buildSimilarEventsUrl } from '~/composables/product'
import { kftApiRequest } from '~/utils/kftApiRequest'
import { transformToAlgoliaEvent } from '~/utils/helpers'
import { typesenseSearch } from '~/utils/typesenseClient'
import { buildVectorSimilarEventsQuery } from '~/utils/typesenseSimilarEventsQuery'
import { getEventListIndexName } from '~/utils/searchEngineHelpers'
import { toSearchEngineEventItem } from '~/utils/adaptTypesenseEventDocument'

/** Vector nearest-neighbour similar events from Typesense (KFT-4032). */
const fetchSimilarEventsTypesense = async (
  permalink: string,
  collection: string,
  handle: TypesenseClientHandle
): Promise<SearchEngineEventItem[]> => {
  const source = await fetchTypesenseSourceEvent(permalink, collection, handle)
  if (!source) return []

  const params = buildVectorSimilarEventsQuery(source, collection)
  // Static per event (the geo sort is the event's own location, not the
  // viewer's), so every viewer of this event sends the same request.
  const result = await typesenseSearch<Record<string, unknown>>(params, {
    ...handle,
    useCache: true,
  })

  if (import.meta.dev) {
    console.debug(
      `[KFT-4032] similar events (vector): ${result.search_time_ms}ms server, ${result.hits?.length ?? 0} hits`
    )
  }

  return (result.hits ?? []).map(({ document }) =>
    toSearchEngineEventItem(document)
  )
}

/**
 * Similar events for the event page, per the shared search engine (KFT-4115):
 * `algolia` → KFT API `/similar` endpoint, `typesense` → Typesense vector
 * query. The engine is in the key, so an A/B force after hydration refetches
 * from the other backend — and only one backend is ever hit per engine.
 */
export const useSimilarEvents = (permalink: string) => {
  const { locale } = useI18n()
  const engine = useSearchEngine({ clientOnly: true })
  const typesenseHandle = useTypesenseHandle()

  return useAsyncData<SearchEngineEventItem[]>(
    () => `similar-events-${engine.value}-${permalink}`,
    async () => {
      if (!permalink) return []

      if (engine.value === 'typesense') {
        return fetchSimilarEventsTypesense(
          permalink,
          getEventListIndexName(locale.value),
          typesenseHandle
        )
      }

      const response = await kftApiRequest<EventDescription[]>(
        buildSimilarEventsUrl(permalink),
        { method: 'GET' }
      )
      // Same partial shape the KFT API path always produced (useFetch transform).
      return transformToAlgoliaEvent(response) as SearchEngineEventItem[]
    },
    { server: false, default: () => [], dedupe: 'defer' }
  )
}
